import React from 'react';

/**
 * Full-page skeleton that mirrors the dashboard overview layout.
 * Shown on first open until overview/activity data is loaded, so the
 * dashboard never shows a mixed or "bad" loading state.
 */
export const DashboardSkeleton: React.FC = () => {
  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-screen-2xl mx-auto space-y-4 sm:space-y-6" dir="rtl">
      {/* 8 KPI cards row — same grid as real dashboard */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3 sm:gap-4"
        aria-hidden
      >
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="rounded-xl sm:rounded-2xl border border-gray-100 bg-white shadow-sm p-3 sm:p-4 min-h-[88px] sm:min-h-[100px] flex flex-col gap-2"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0 space-y-2">
                <div className="h-3 w-16 rounded-lg bg-gray-100 animate-pulse" />
                <div className="h-6 w-20 rounded-lg bg-gray-200 animate-pulse" />
                <div className="h-3 w-12 rounded-lg bg-gray-100 animate-pulse" />
              </div>
              <div className="w-10 h-10 rounded-xl bg-gray-100 animate-pulse shrink-0" />
            </div>
          </div>
        ))}
      </section>

      <div className="space-y-4 sm:space-y-6">
        {/* Pipeline section placeholder */}
        <div className="rounded-xl sm:rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
          <div className="p-3 sm:p-4 border-b border-gray-100">
            <div className="h-4 sm:h-5 w-32 sm:w-40 rounded-lg bg-gray-100 animate-pulse" />
            <div className="h-3 w-40 sm:w-56 rounded-lg bg-gray-50 animate-pulse mt-2" />
          </div>
          <div className="p-3 sm:p-4">
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="h-20 sm:h-24 w-20 sm:w-28 rounded-lg sm:rounded-xl bg-gray-50 animate-pulse"
                  style={{ animationDelay: `${i * 80}ms` }}
                />
              ))}
            </div>
            <div className="mt-4 flex justify-start">
              <div className="h-9 w-28 rounded-xl bg-gray-100 animate-pulse" />
            </div>
          </div>
        </div>

        {/* Team performance section — list skeleton matching real layout */}
        <div className="rounded-xl sm:rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
          <div className="p-3 sm:p-4 border-b border-gray-100 flex items-center justify-between gap-2">
            <div className="h-4 sm:h-5 w-24 sm:w-32 rounded-lg bg-gray-100 animate-pulse" />
            <div className="h-8 sm:h-9 w-20 sm:w-28 rounded-lg sm:rounded-xl bg-gray-100 animate-pulse shrink-0" />
          </div>
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-3 sm:gap-4 py-2"
              >
                <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-gray-100 animate-pulse shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="h-4 w-24 sm:w-32 rounded-lg bg-gray-100 animate-pulse" />
                  <div className="h-3 w-20 sm:w-24 rounded-lg bg-gray-50 animate-pulse" />
                </div>
                <div className="h-7 w-14 sm:h-8 sm:w-16 rounded-lg bg-gray-100 animate-pulse shrink-0" />
              </div>
            ))}
          </div>
        </div>

        {/* Bottom row: tasks & follow-ups + calendar area placeholders */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div className="rounded-xl sm:rounded-2xl border border-gray-100 bg-white shadow-sm p-3 sm:p-5">
            <div className="h-4 sm:h-5 w-28 sm:w-36 rounded-lg bg-gray-100 animate-pulse mb-3 sm:mb-4" />
            <div className="space-y-2 sm:space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-10 sm:h-12 rounded-lg sm:rounded-xl bg-gray-50 animate-pulse" />
              ))}
            </div>
          </div>
          <div className="rounded-xl sm:rounded-2xl border border-gray-100 bg-white shadow-sm p-3 sm:p-5">
            <div className="h-4 sm:h-5 w-32 sm:w-40 rounded-lg bg-gray-100 animate-pulse mb-3 sm:mb-4" />
            <div className="h-48 sm:h-64 rounded-lg sm:rounded-xl bg-gray-50 animate-pulse" />
          </div>
        </div>
      </div>

      {/* Subtle loading label for screen readers and clarity */}
      <p className="sr-only">טוען דשבורד...</p>
    </div>
  );
};
